import { useState } from 'react'
import { reviewApi, type ReviewQuote } from '../api/review'
import { readApiError } from '../lib/apiError'
import { TagAutocomplete } from './TagAutocomplete'

const MISSING_LABELS: Record<string, string> = {
  text: 'kein Text',
  verified: 'ungeprüft',
  tags: 'keine Tags',
  images: 'kein Bild',
  page_numbers: 'Seitenzahl fehlt',
}

interface ReviewQuoteItemProps {
  quote: ReviewQuote
  category: string
  onSaved: () => void
}

export const ReviewQuoteItem = ({ quote, category, onSaved }: ReviewQuoteItemProps) => {
  const [text, setText] = useState(quote.text_content ?? '')
  const [verified, setVerified] = useState(quote.text_status === 'manual_verified')
  const [tags, setTags] = useState<string[]>(quote.tags.map((t) => t.name))
  const [pages, setPages] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      quote.images.map((img) => [img.id, img.page_number != null ? String(img.page_number) : ''])
    )
  )
  const [files, setFiles] = useState<File[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sourceLabel = [
    quote.source?.title,
    quote.source?.authors?.length ? quote.source.authors.map((a) => a.name).join(', ') : null,
  ]
    .filter(Boolean)
    .join(' — ')

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    try {
      if (category === 'quote_no_images') {
        for (const file of files) {
          await reviewApi.addImage(quote.id, file)
        }
      } else if (category === 'quote_image_no_page') {
        for (const img of quote.images) {
          const next = pages[img.id] ?? ''
          const current = img.page_number != null ? String(img.page_number) : ''
          if (next !== current) {
            await reviewApi.updateImagePage(quote.id, img.id, next)
          }
        }
      } else {
        await reviewApi.updateQuote(quote.id, {
          source_id: quote.source_id,
          text_content: text,
          text_status: verified ? 'manual_verified' : 'ai_generated',
          tag_names: tags,
        })
      }
      onSaved()
    } catch (err) {
      setError(readApiError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const canSave = category === 'quote_no_images' ? files.length > 0 : true

  return (
    <article className="review-item">
      <header className="review-item-header">
        <div>
          <span className="review-item-id">Zitat #{quote.id}</span>
          {sourceLabel && <span className="review-item-source">{sourceLabel}</span>}
        </div>
        <div className="review-badges">
          {quote.missing.map((key) => (
            <span key={key} className="review-badge">
              {MISSING_LABELS[key] ?? key}
            </span>
          ))}
          <a
            href={`/quote/${quote.id}`}
            target="_blank"
            rel="noreferrer"
            className="review-item-link"
          >
            Öffnen ↗
          </a>
        </div>
      </header>

      <div className="review-item-body">
        {quote.images.length > 0 && category !== 'quote_image_no_page' && (
          <div className="review-thumbs">
            {quote.images.map((img) => (
              <a key={img.id} href={`/${img.image_path}`} target="_blank" rel="noreferrer">
                <img src={`/${img.thumbnail_path}`} alt={`Bild zu Zitat ${quote.id}`} />
              </a>
            ))}
          </div>
        )}

        <div className="review-fields">
          {(category === 'quote_no_text' || category === 'quote_unverified') && (
            <>
              <label className="review-label" htmlFor={`text-${quote.id}`}>
                Text
              </label>
              <textarea
                id={`text-${quote.id}`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                disabled={isSaving}
                placeholder="Text zum Bild erfassen…"
              />
              <label className="review-checkbox">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                  disabled={isSaving}
                />
                Text geprüft
              </label>
            </>
          )}

          {category === 'quote_no_tags' && (
            <>
              {text && <blockquote className="review-preview">{text.substring(0, 300)}</blockquote>}
              <label className="review-label">Tags</label>
              <TagAutocomplete selectedTags={tags} onChange={setTags} disabled={isSaving} />
            </>
          )}

          {category === 'quote_no_images' && (
            <>
              {text && <blockquote className="review-preview">{text.substring(0, 300)}</blockquote>}
              <label className="review-label" htmlFor={`files-${quote.id}`}>
                Bilder hinzufügen
              </label>
              <input
                id={`files-${quote.id}`}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                disabled={isSaving}
              />
            </>
          )}

          {category === 'quote_image_no_page' && (
            <div className="review-page-grid">
              {quote.images.map((img) => (
                <div key={img.id} className="review-page-item">
                  <a href={`/${img.image_path}`} target="_blank" rel="noreferrer">
                    <img src={`/${img.thumbnail_path}`} alt={`Bild ${img.id}`} />
                  </a>
                  <input
                    type="number"
                    min={1}
                    value={pages[img.id] ?? ''}
                    onChange={(e) => setPages({ ...pages, [img.id]: e.target.value })}
                    placeholder="Seite"
                    disabled={isSaving}
                  />
                </div>
              ))}
            </div>
          )}

          {error && <p className="review-error">{error}</p>}

          <div className="review-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || !canSave}
            >
              {isSaving ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </article>
  )
}
